import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useLazyGetVendorTransactionReportDetailQuery } from "store/services/reportApi";
import ExportVendorTransactionDetail from "./ExportVendorTransactionDetail";

const breadcrumbs = [
  { title: "Reports", path: "/app/crm/reports/list" },
  {
    title: "Vendor Transaction Reports",
    path: "/app/crm/reports/vendor-transaction-report",
  },
  { title: "Vendor Transaction Report Detail" },
];

const getStatusStyle = (status) => {
  const styles = {
    "Not Paid": { backgroundColor: "#F8C471", textColor: "#D35400" },
    Paid: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const VendorTransactionReportDetail = () => {
  const { vendorId } = useParams();

  const [searchParams] = useSearchParams();

  const [paymentStatus, setPaymentStatus] = useState(
    searchParams.get("paymentStatus") || "all"
  );

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [
    fetchVendorTransactionReportDetail,
    { data: detailRes, isLoading, isFetching },
  ] = useLazyGetVendorTransactionReportDetailQuery();

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => row?.shipmentId || 0,
    },

    {
      name: "Amount",
      selector: (row) => row?.charge || 0,
      cell: (row) =>
        row?.charge ? `Rs.${Math.abs(row.charge).toFixed(2)}` : "N/A",
      sortable: true,
    },

    {
      name: "Charge Type",
      selector: (row) => row?.chargeType || "",
      cell: (row) => row?.chargeType || "N/A",
      sortable: true,
    },

    {
      name: "Date",
      selector: (row) => row?.createdAt || "",
      cell: (row) => <AppTableDateTime date={row?.createdAt} dateOnly />,
      sortable: true,
    },

    {
      name: "Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => (
        <Chip
          label={row?.paymentStatus?.toUpperCase() || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getStatusStyle(row?.paymentStatus).backgroundColor,
            color: getStatusStyle(row?.paymentStatus).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": {
              fontSize: "0.6rem",
              padding: "3px 5px",
              lineHeight: "14px",
            },
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchVendorTransactionReportDetail({
      vendorProfile: vendorId,
      limit: perPage,
      page: currentPage,
      ...(paymentStatus !== "all" && { paymentStatus }),
    });
  }, [
    currentPage,
    fetchVendorTransactionReportDetail,
    paymentStatus,
    perPage,
    vendorId,
  ]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error />
      <BreadcrumbLayout.Paper>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Vendor Transaction Report Detail
              <p>
                <span style={{ fontWeight: "bold", marginTop: "20px" }}>
                  {detailRes?.data?.[0]?.vendorProfile?.ownerName ||
                    detailRes?.data?.[0]?.vendorProfile?.shopName ||
                    ""}
                </span>

                <br />
                <span>
                  {detailRes?.data?.[0]?.vendorProfile?.shopName
                    ? detailRes?.data?.[0]?.vendorProfile?.shopName
                    : ""}
                </span>
              </p>
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: 1, justifyContent: "flex-end" }}
            >
              <BaseSelect
                label="Status"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                options={[
                  { label: "All", value: "all" },
                  { label: "Not Paid", value: "Not Paid" },
                  { label: "Paid", value: "Paid" },
                  { label: "Collected", value: "Collected" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 210 }}
              />

              <ExportVendorTransactionDetail vendorId={vendorId} />
            </Stack>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailRes?.data?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No data available for the selected filters.
              </Typography>
            </Box>
          ) : (
            <AppTable
              columns={columns}
              data={detailRes?.data ?? []}
              progressPending={isLoading || isFetching}
              paginationServer
              paginationPerPage={perPage}
              paginationTotalRows={detailRes?.totalItems ?? 0}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            />
          )}
        </Box>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VendorTransactionReportDetail;
